/**
 * The pure half of the PHONE production-record wizard
 * (`components/production/mobile/mobile-record-wizard.tsx`, plan-06 lane C).
 *
 * The phone does not type eleven columns per row: it photographs the filled
 * sheets, lets the model read them, then reviews one mealtime at a time as
 * cards and confirms each ("Review Breakfast → Confirm → Review Lunch →
 * Confirm", `mobile.md`). These helpers decide WHICH steps are reviewed and in
 * what order, how a photo queue grows, and what the two phase labels say. No
 * React, no DOM, no fetch.
 */

/**
 * The endpoint's `MAX_SCAN_IMAGES`: two frames per landscape page across the
 * six mealtimes the platform schedules, since the wizard sends every unsigned
 * mealtime's sheets in ONE request. Raise both sides together.
 */
export const MOBILE_WIZARD_PHOTO_CAP = 12

/** The step facts the wizard orders by. Structurally `RecordStep`, so the flow passes its steps as they are. */
export interface WizardStepLike {
  key: string
}

/**
 * The steps still to review, in the flow's own order: every step the day has
 * that is not signed (or, for a record-less step, whose trucks are not all
 * received). A signed step is never re-reviewed on the phone.
 */
export function reviewSteps<T extends WizardStepLike>(steps: readonly T[], done: (step: T) => boolean): T[] {
  return steps.filter((step) => !done(step))
}

/**
 * Where the wizard stands after `currentKey` left the unsigned list (it was
 * just confirmed) or was never in it: the first unsigned step AFTER the
 * current one's position, else the first unsigned step overall, else null
 * (everything is signed). With `currentKey` still unsigned it is returned
 * itself, so a caller can derive "the step under review" from one call.
 */
export function nextReviewStep<T extends WizardStepLike>(
  steps: readonly T[],
  done: (step: T) => boolean,
  currentKey: string | null,
): T | null {
  const pending = reviewSteps(steps, done)
  if (pending.length === 0) {
    return null
  }
  const current = currentKey === null ? undefined : pending.find((step) => step.key === currentKey)
  if (current) {
    return current
  }
  const position = currentKey === null ? -1 : steps.findIndex((step) => step.key === currentKey)
  const after = position === -1 ? undefined : pending.find((step) => steps.indexOf(step) > position)
  return after ?? pending[0] ?? null
}

/** The wizard's identity for a picked file: a phone re-offers the same shot under the same name and size. */
export function photoKey(file: Pick<File, 'name' | 'size'>): string {
  return `${file.name}:${file.size}`
}

export interface MergedPhotos<T extends Pick<File, 'name' | 'size'>> {
  photos: T[]
  /** Set when the cap cut the pick short; already phrased for display. Null otherwise. */
  capMessage: string | null
}

/**
 * Add `incoming` to the queue: a photo already queued (same name and size) is
 * skipped, and the queue never grows past `cap`. The result says when the cap
 * dropped something, so the screen can tell the worker rather than silently
 * losing a page.
 */
export function mergePhotos<T extends Pick<File, 'name' | 'size'>>(
  existing: readonly T[],
  incoming: readonly T[],
  cap: number = MOBILE_WIZARD_PHOTO_CAP,
): MergedPhotos<T> {
  const seen = new Set(existing.map(photoKey))
  const photos = [...existing]
  let dropped = false
  for (const file of incoming) {
    const key = photoKey(file)
    if (seen.has(key)) {
      continue
    }
    if (photos.length >= cap) {
      dropped = true
      break
    }
    seen.add(key)
    photos.push(file)
  }
  return { photos, capMessage: dropped ? `You can send up to ${cap} photos at a time.` : null }
}

/** The reading phase's title: `Reading 3 photos…`, or `Reading the sheet…` for one. Same words as the scan card. */
export function readingTitle(count: number): string {
  return count > 1 ? `Reading ${count} photos…` : 'Reading the sheet…'
}

/**
 * The primary button of a review step. A record step signs (`Confirm
 * Breakfast` once the step's title is known, PROD-1511; `Signing…` while the
 * chain runs); a record-less step only receives its trucks, and says so
 * (`Confirm deliveries`, `Saving…`).
 */
export function confirmLabel(input: { hasRecord: boolean; busy: boolean; title?: string | null }): string {
  if (input.busy) {
    return input.hasRecord ? 'Signing…' : 'Saving…'
  }
  if (!input.hasRecord) {
    return 'Confirm deliveries'
  }
  return input.title ? `Confirm ${input.title}` : 'Confirm'
}

const COUNT_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six']

/**
 * The start screen's one line under its question (PROD-1511): what this
 * record covers, `Two mealtimes: Breakfast and Lunch.`
 */
export function mealtimesLine(titles: readonly string[]): string {
  const count = COUNT_WORDS[titles.length] ?? String(titles.length)
  const noun = titles.length === 1 ? 'mealtime' : 'mealtimes'
  if (titles.length === 0) {
    return `${count} ${noun} to review.`
  }
  const list = titles.length === 1 ? titles[0]! : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]!}`
  return `${count} ${noun}: ${list}.`
}

/** `Step 2 of 2`: the step's position among ALL the day's mealtimes, so a signed Breakfast still counts. */
export function stepCounter<T extends WizardStepLike>(steps: readonly T[], key: string): string {
  const index = steps.findIndex((step) => step.key === key)
  return `Step ${index === -1 ? 1 : index + 1} of ${Math.max(steps.length, 1)}`
}
